import { Radio, Select, Spin } from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import { Context } from "../../context";
import { convertIdToKey } from "../../utils";
import MovieUploader from "./movie";
import SeriesUploader from "./series";
import EpisodeUploader from "./episode";
import SongUploader from "./song";

const CONTENT_TYPES = {
    MOVIE: "movies",
    SERIES: "series",
    EPISODE: "episodes",
    SONG: "songs"
};

const ContentUploader = () => {
    const [contentType, setContentType] = useState(CONTENT_TYPES.MOVIE);
    const [allContent, setAllContent] = useState({
        [CONTENT_TYPES.MOVIE]: [],
        [CONTENT_TYPES.SERIES]: [],
        [CONTENT_TYPES.EPISODE]: [],
        [CONTENT_TYPES.SONG]: [],
    });
    const [contentList, setContentList] = useState([]);
    const [selectedContent, setSelectedContent] = useState(null);
    const [episodesList, setEpisodesList] = useState([]);
    const [selectedEpisode, setSelectedEpisode] = useState(null);
    const [loading, setLoading] = useState(true);
    const { token, apiUrl } = useContext(Context);

    // Fetch all content types on mount
    useEffect(() => {
        const fetchAllContent = async () => {
  
            const movieRes = await fetch(`${apiUrl}/${CONTENT_TYPES.MOVIE}`, { headers: { Authorization: `Bearer ${token}` } });
            const movieData = convertIdToKey((await movieRes.json())[CONTENT_TYPES.MOVIE]);

            const seriesRes = await fetch(`${apiUrl}/${CONTENT_TYPES.SERIES}`, { headers: { Authorization: `Bearer ${token}` } });
            const seriesData = convertIdToKey((await seriesRes.json())[CONTENT_TYPES.SERIES]);

            const episodeRes = await fetch(`${apiUrl}/series`, { headers: { Authorization: `Bearer ${token}` } });
            const episodeData = convertIdToKey((await episodeRes.json()).series);

            const songRes = await fetch(`${apiUrl}/${CONTENT_TYPES.SONG}`, { headers: { Authorization: `Bearer ${token}` } });
            const songData = convertIdToKey((await songRes.json())[CONTENT_TYPES.SONG]);

            setAllContent({
                [CONTENT_TYPES.MOVIE]: movieData,
                [CONTENT_TYPES.SERIES]: seriesData,
                [CONTENT_TYPES.EPISODE]: episodeData, // episodes are nested under series
                [CONTENT_TYPES.SONG]: songData
            });

            // Set initial content list for default type
            setContentList(movieData);
            setLoading(false);
        };

        fetchAllContent();

    }, []);

    const handleContentUpdate = (updated) => {
        if (contentType === CONTENT_TYPES.EPISODE) {

            const updatedWithSeriesData = {
                ...updated,
                seriesId: selectedContent?.key ?? null,
                episodeNumber: updated.number,
                seasonNumber: updated.season,
                contentType: "episode"
            }
            setSelectedEpisode(updatedWithSeriesData)
        } else {
            setSelectedContent(updated)
        }
    }

    // Update contentList when contentType changes
    useEffect(() => {
        if (contentType === CONTENT_TYPES.EPISODE) {
            setContentList(allContent[CONTENT_TYPES.EPISODE]);
        } else {
            setContentList(allContent[contentType]);
        }
        setSelectedContent(null);
        setSelectedEpisode(null);
        setEpisodesList([]);
    }, [contentType, allContent]);

    const handleContentTypeChange = useCallback((e) => {
        setContentType(e.target.value);
    }, []);

    const handleContentSelection = useCallback((contentId) => {
        const activeContent = contentList.find(each => each.key === contentId);

        if (contentType === CONTENT_TYPES.EPISODE && activeContent) {
            const episodes = activeContent?.episodes ?? [];
            setEpisodesList(episodes);
        }

        setSelectedContent(activeContent);
    }, [contentList, contentType]);

    const handleEpisodeSelection = useCallback((contentName) => {
        const activeEpisode = episodesList.find(each => each.name === contentName);
        if (!activeEpisode) return;

        setSelectedEpisode({
            ...activeEpisode,
            seriesId: selectedContent?.key ?? null,
            episodeNumber: activeEpisode.number,
            seasonNumber: activeEpisode.season,
            contentType: "episode"
        });
    }, [episodesList, selectedContent?.key]);

    return (
        <div style={{ padding: 20 }}>
            <h1>Upload Content</h1>

            <Radio.Group
                className="content-type-option"
                value={contentType}
                onChange={handleContentTypeChange}
            >
                <Radio value={CONTENT_TYPES.MOVIE}>Movie</Radio>
                <Radio value={CONTENT_TYPES.SERIES}>Series</Radio>
                <Radio value={CONTENT_TYPES.EPISODE}>Episode</Radio>
                <Radio value={CONTENT_TYPES.SONG}>Song</Radio>
            </Radio.Group>

            {
                loading === true ? (
                    <div style={{ textAlign: "center", padding: "20px" }}>
                        <Spin tip="Loading Contents" />
                    </div>
                ) : (
                    <>
                        <Select
                            style={{ width: 300 }}
                            placeholder={`Select ${contentType}`}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            value={selectedContent?.title}
                            onChange={handleContentSelection}
                            onClear={() => setSelectedContent(null)}
                        >
                            {contentList?.map(content => (
                                <Select.Option key={content.key} value={content.key}>
                                    {content.title}
                                </Select.Option>
                            ))}
                        </Select>

                        {selectedContent && contentType === CONTENT_TYPES.EPISODE && (
                            <Select
                                style={{ width: 300, marginLeft: 40 }}
                                placeholder="Select Episode"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                value={selectedEpisode?.name}
                                onChange={handleEpisodeSelection}
                                onClear={() => setSelectedEpisode(null)}
                            >
                                {episodesList?.map((ep, index) => (
                                    <Select.Option key={index} value={ep.name}>
                                        {ep.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        )}

                        {selectedContent && contentType === CONTENT_TYPES.MOVIE && <MovieUploader selectedContent={selectedContent} updateContent={handleContentUpdate} />}
                        {selectedContent && contentType === CONTENT_TYPES.SERIES && <SeriesUploader selectedContent={selectedContent} updateContent={handleContentUpdate} />}
                        {selectedEpisode && contentType === CONTENT_TYPES.EPISODE && <EpisodeUploader selectedEpisode={selectedEpisode} updateContent={handleContentUpdate} />}
                        {selectedContent && contentType === CONTENT_TYPES.SONG && <SongUploader selectedContent={selectedContent} updateContent={handleContentUpdate} />}
                    </>
                )
            }

        </div>
    );
};

export default ContentUploader;
